// Daily schedule helper functions
import { getWeekStartForDate } from '../scheduleGenerators/dateHelpers';

/**
 * Get residents for a specific date, grouped by rotation
 * @param {Date} date - The date to get residents for
 * @param {Object} schedules - Rotation schedules by PGY level
 * @param {Array} offServiceResidents - Array of off-service resident objects
 * @param {Object} weekendSchedules - Weekend call schedules (optional)
 * @returns {Object} Residents grouped by rotation
 */
export const getResidentsForDate = (date, schedules, offServiceResidents = [], weekendSchedules = {}) => {
  // Validate date
  if (!date || isNaN(date.getTime())) {
    return {};
  }

  const dateStr = `${date.getFullYear()}-${String(
    date.getMonth() + 1
  ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const monthStr = `${date.getFullYear()}-${String(
    date.getMonth() + 1
  ).padStart(2, "0")}`;
  const residentsByRotation = {};

  // Get all unique rotations from the rotation schedule
  const allRotations = new Set();
  Object.values(schedules).forEach((scheduleData) => {
    if (!scheduleData.schedule) return;
    const isWeekly = scheduleData.scheduleType === 'weekly';
    const isBlocks = scheduleData.scheduleType === 'blocks';
    Object.values(scheduleData.schedule).forEach((residentData) => {
      if (!residentData.rotations) return;
      if (isWeekly || isBlocks) {
        const weekKey = getWeekStartForDate(date);
        const rotation = residentData.rotations[weekKey];
        if (rotation) allRotations.add(rotation);
      } else {
        const rotation = residentData.rotations[monthStr];
        if (rotation) allRotations.add(rotation);
      }
    });
  });

  // Add rotations from off-service residents
  offServiceResidents.forEach(osr => {
    const osrMonth = (osr.month || '').trim();
    if (osrMonth === monthStr && osr.rotationName) {
      allRotations.add(osr.rotationName.trim());
    }
  });

  // Initialize rotations
  Array.from(allRotations).forEach((rotation) => {
    residentsByRotation[rotation] = [];
  });

  // Get residents for each rotation
  Object.entries(schedules).forEach(([_, scheduleData]) => {
    if (!scheduleData.schedule) return;
    const isWeekly = scheduleData.scheduleType === 'weekly';
    const isBlocks = scheduleData.scheduleType === 'blocks';
    Object.entries(scheduleData.schedule).forEach(
      ([residentId, residentData]) => {
        if (!residentData.rotations) return;
        const rotation = (isWeekly || isBlocks)
          ? residentData.rotations[getWeekStartForDate(date)]
          : residentData.rotations[monthStr];
        if (rotation) {
          if (!residentsByRotation[rotation].some((r) => r.id === residentId)) {
            residentsByRotation[rotation].push({
              id: residentId,
              name: residentData.name,
              year: residentData.year,
            });
          }
        }
      }
    );
  });

  // Add off-service residents for this date
  offServiceResidents.forEach(osr => {
    const osrMonth = (osr.month || '').trim();
    const osrRotation = (osr.rotationName || '').trim();
    
    // Check if this off-service resident matches the date's month
    if (osrMonth === monthStr && osrRotation) {
      // Initialize rotation if it doesn't exist
      if (!residentsByRotation[osrRotation]) {
        residentsByRotation[osrRotation] = [];
      }
      
      // Check if this off-service resident is already added
      const offServiceId = `off-service-${osr.id}`;
      if (!residentsByRotation[osrRotation].some((r) => r.id === offServiceId)) {
        residentsByRotation[osrRotation].push({
          id: offServiceId,
          name: osr.residentName,
          year: osr.pgyLevel,
          isOffService: true
        });
      }
    }
  });

  // For Saturday and Sunday, add weekend call residents
  const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
  if (dayOfWeek === 0 || dayOfWeek === 6) { // Sunday or Saturday
    Object.entries(weekendSchedules).forEach(([rotation, weekendData]) => {
      // Check if this rotation has weekend call data for this specific date
      if (weekendData[dateStr]) {
        const weekendResidents = weekendData[dateStr].residents || [];
        
        // If this rotation doesn't exist in residentsByRotation, create it
        if (!residentsByRotation[rotation]) {
          residentsByRotation[rotation] = [];
        }
        
        // Add weekend call residents to the rotation
        weekendResidents.forEach((weekendResident) => {
          // Check if resident is already in the list
          const existingResident = residentsByRotation[rotation].find(
            (r) => r.id === weekendResident.id
          );
          
          if (!existingResident) {
            residentsByRotation[rotation].push({
              id: weekendResident.id,
              name: weekendResident.name,
              year: weekendResident.year,
              isOnCall: true, // Mark as on call
            });
          } else {
            // If resident already exists, mark them as on call
            existingResident.isOnCall = true;
          }
        });
      }
    });
  }

  return residentsByRotation;
};

